import { Controller } from "@hotwired/stimulus";

// Notification settings sheet: searches rows (hiding empty categories and
// auto-expanding matches), collapses/expands all categories, and keeps each
// category's "All on / N on / All off" summary in sync the moment a segment is
// tapped — before the PATCH round-trips. Connects to
// data-controller="notification-settings".
export default class extends Controller {
  static targets = ["category", "input", "empty", "toggleLabel"];

  declare readonly categoryTargets: HTMLDetailsElement[];
  declare readonly inputTarget: HTMLInputElement;
  declare readonly emptyTarget: HTMLElement;
  declare readonly hasEmptyTarget: boolean;
  declare readonly toggleLabelTarget: HTMLElement;
  declare readonly hasToggleLabelTarget: boolean;

  filter() {
    const query = this.inputTarget.value.toLowerCase().trim();
    let anyVisible = false;

    this.categoryTargets.forEach((category) => {
      let matches = 0;
      category.querySelectorAll<HTMLElement>("[data-search]").forEach((row) => {
        const hit = (row.dataset.search || "").includes(query);
        row.classList.toggle("hidden", !hit);
        if (hit) matches += 1;
      });

      category.classList.toggle("hidden", matches === 0);
      if (matches > 0) anyVisible = true;
      // While searching, open matching categories so hits are never hidden.
      if (query) category.open = matches > 0;
    });

    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", anyVisible);
  }

  toggleAll() {
    const collapse = this.categoryTargets.some((category) => category.open);
    this.categoryTargets.forEach((category) => {
      category.open = !collapse;
    });
    this.updateToggleLabel();
  }

  syncToggleLabel() {
    this.updateToggleLabel();
  }

  selectSegment(event: Event) {
    const link = event.currentTarget as HTMLElement;
    const row = link.closest<HTMLElement>("[data-enabled]");
    if (row) row.dataset.enabled = link.dataset.level !== "0" ? "true" : "false";

    const category = link.closest("details");
    if (category) this.refreshSummary(category);
  }

  private refreshSummary(category: HTMLElement) {
    const summary = category.querySelector<HTMLElement>("[data-notif-summary]");
    if (!summary) return;

    const rows = Array.from(category.querySelectorAll<HTMLElement>("[data-enabled]"));
    const on = rows.filter((row) => row.dataset.enabled === "true").length;

    if (on === 0) summary.textContent = summary.dataset.allOff ?? "";
    else if (on === rows.length) summary.textContent = summary.dataset.allOn ?? "";
    else summary.textContent = (summary.dataset.countOn ?? "").replace("%{count}", String(on));
  }

  private updateToggleLabel() {
    if (!this.hasToggleLabelTarget) return;

    const anyOpen = this.categoryTargets.some((category) => category.open);
    this.toggleLabelTarget.textContent = anyOpen
      ? this.toggleLabelTarget.dataset.collapseLabel ?? ""
      : this.toggleLabelTarget.dataset.expandLabel ?? "";
  }
}
